import React, { useCallback, useEffect, useState } from "react";
import FeatureUnavailable from "ui/components/Availability/FeatureUnavailable/FeatureUnavailable";
import { AvailabilityResult } from "services/api/types";

interface UseAvailabilityOptions {
  apiFn: () => Promise<AvailabilityResult>;
  enabled: boolean;
}

interface RenderUnavailableProps {
  title: string;
  onClose: () => void;
}

export const useAvailability = ({ apiFn, enabled }: UseAvailabilityOptions) => {
  const [isAvailable, setIsAvailable] = useState<boolean>(true);
  const [message, setMessage] = useState<string>("");
  const [isChecking, setIsChecking] = useState<boolean>(false);

  useEffect(() => {
    if (!enabled) {
      setIsAvailable(true);
      setMessage("");
      return;
    }

    const check = async () => {
      setIsChecking(true);

      try {
        const result = await apiFn();
        setIsAvailable(result.isAvailable);
        setMessage(result.message ?? "");
      } catch {
        setIsAvailable(false);
        setMessage("Greška pri provjeri dostupnosti.");
      } finally {
        setIsChecking(false);
      }
    };

    check();
  }, [enabled]);

  const renderUnavailable = useCallback(
    ({ title, onClose }: RenderUnavailableProps): React.ReactNode => {
      if (isAvailable || isChecking) return null;

      return React.createElement(FeatureUnavailable, {
        show: true,
        title,
        description: message,
        onClose,
      });
    },
    [isAvailable, isChecking, message]
  );

  return { isAvailable, message, isChecking, renderUnavailable };
};
